import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  Show,
  TabbedShowLayout,
  Tab,
  ReferenceManyField,
  DateField,
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  Create,
  Button,
  useNotify,
  useRefresh,
  useRecordContext,
  BooleanInput,
  NumberInput,
  DeleteButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  SaveButton,
  useCreate,
  useUpdate,
  useGetOne
} from 'react-admin';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Input,
  Checkbox,
  FormControlLabel,
  TextField as MuiTextField,
  Rating,
  Chip,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';

// Custom Edit Button Component
const CustomEditButton = ({ onEdit }) => {
  const commentRecord = useRecordContext();
  console.log('CustomEditButton commentRecord:', commentRecord); // Debug log
  return (
    <Button
      label="Edit"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(commentRecord);
      }}
      startIcon={<EditIcon />}
    />
  );
};

// Comments Management Component for Apartment Show
export const CommentsManagement = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  
  const [showForm, setShowForm] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    rating: 5,
    text: '',
    is_published: true
  });

  const handleCreateComment = () => {
    setEditingComment(null);
    setFormData({
      name: '',
      date: new Date().toISOString().split('T')[0],
      rating: 5,
      text: '',
      is_published: true
    });
    setShowForm(true);
  };

  const handleEditComment = (comment) => {
    console.log('Editing comment:', comment); // Debug log
    setEditingComment(comment);
    
    // Format date for input field (YYYY-MM-DD)
    let formattedDate = new Date().toISOString().split('T')[0];
    if (comment.date) {
      // If date is in different format, convert it
      const dateObj = new Date(comment.date);
      if (!isNaN(dateObj.getTime())) {
        formattedDate = dateObj.toISOString().split('T')[0];
      }
    }
    
    setFormData({
      name: comment.name || '',
      date: formattedDate,
      rating: parseFloat(comment.rating) || 5,
      text: comment.text || '',
      is_published: comment.is_published !== undefined ? comment.is_published : true
    });
    setShowForm(true);
  };

  const handleSaveComment = async () => {
    try {
      const commentData = {
        name: formData.name,
        date: formData.date,
        rating: parseFloat(formData.rating),
        text: formData.text,
        is_published: Boolean(formData.is_published),
        apartment: record.id
      };

      console.log('Saving comment data:', commentData); // Debug log
      console.log('Editing comment:', editingComment); // Debug log

      if (editingComment) {
        await update('comments', { id: editingComment.id, data: commentData });
        notify('Comment updated successfully', { type: 'success' });
      } else {
        await create('comments', { data: commentData });
        notify('Comment created successfully', { type: 'success' });
      }
      
      setShowForm(false);
      setEditingComment(null);
      refresh();
    } catch (error) {
      console.error('Error saving comment:', error);
      notify('Error saving comment', { type: 'error' });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingComment(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Comments & Reviews
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateComment}
          color="primary"
        >
          Add Comment
        </Button>
      </Box>

      {/* Comments List */}
      <ReferenceManyField 
        reference="comments" 
        target="apartment" 
        label=""
        sort={{ field: 'date', order: 'DESC' }}
      >
        <Datagrid>
          <TextField source="name" label="Guest Name" />
          <DateField source="date" label="Review Date" />
          <RatingField source="rating" label="Rating" />
          <TextField source="text" label="Review Text" />
          <BooleanField source="is_published" label="Published" />
          <CustomEditButton onEdit={handleEditComment} />
          <DeleteButton />
        </Datagrid>
      </ReferenceManyField>

      {/* Add/Edit Form Dialog */}
      <Dialog open={showForm} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingComment ? 'Edit Comment' : 'Add New Comment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MuiTextField
                  fullWidth
                  label="Guest Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiTextField
                  fullWidth
                  label="Review Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography>Rating:</Typography>
                  <Rating
                    value={parseFloat(formData.rating) || 0}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, rating: newValue || 0 });
                    }}
                    precision={0.5}
                    size="large"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {parseFloat(formData.rating) || 0}/5
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  fullWidth
                  label="Review Text"
                  multiline
                  rows={4}
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.is_published)}
                      onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    />
                  }
                  label="Published (visible on website)"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button 
            onClick={handleSaveComment} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.text}
          >
            {editingComment ? 'Update' : 'Create'} Comment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Custom Rating Field Component
const RatingField = ({ source, record, ...props }) => {
  const contextRecord = useRecordContext();
  const actualRecord = record || contextRecord;
  const value = actualRecord ? actualRecord[source] : 0;
  console.log('RatingField - source:', source, 'record:', record, 'contextRecord:', contextRecord, 'value:', value); // Debug log
  const numericValue = parseFloat(value) || 0;
  return (
    <Box display="flex" alignItems="center">
      <Rating 
        value={numericValue} 
        readOnly 
        precision={0.5}
        size="small"
      />
      <Typography variant="body2" sx={{ ml: 1 }}>
        {numericValue}/5
      </Typography>
    </Box>
  );
};

// Custom Boolean Field for Published Status
const BooleanField = ({ source, record, ...props }) => {
  const contextRecord = useRecordContext();
  const actualRecord = record || contextRecord;
  const value = actualRecord ? actualRecord[source] : false;
  console.log('BooleanField - source:', source, 'record:', record, 'contextRecord:', contextRecord, 'value:', value); // Debug log
  const boolValue = Boolean(value);
  return (
    <Chip 
      label={boolValue ? 'Published' : 'Draft'} 
      color={boolValue ? 'success' : 'default'}
      size="small"
    />
  );
};

// Comments List Component
export const CommentList = props => (
  <List {...props} sort={{ field: 'date', order: 'DESC' }}>
    <Datagrid>
      <TextField source="id" />
      <TextField source="apartment.name" label="Apartment" />
      <TextField source="name" label="Guest Name" />
      <DateField source="date" label="Review Date" />
      <RatingField source="rating" label="Rating" />
      <TextField source="text" label="Review Text" />
      <BooleanField source="is_published" label="Published" />
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

// Comment Show Component
export const CommentShow = props => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Details">
        <TextField source="id" />
        <TextField source="apartment.name" label="Apartment" />
        <TextField source="name" label="Guest Name" />
        <DateField source="date" label="Review Date" />
        <RatingField source="rating" label="Rating" />
        <TextField source="text" label="Review Text" multiline />
        <BooleanField source="is_published" label="Published" />
        <DateField source="created_at" label="Created At" showTime />
        <DateField source="updated_at" label="Updated At" showTime />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

// Comment Edit Component
export const CommentEdit = props => (
  <Edit {...props}>
    <TabbedForm>
      <FormTab label="Details">
        <TextInput disabled source="id" />
        <TextInput source="apartment" label="Apartment ID" />
        <TextInput source="name" label="Guest Name" required />
        <DateInput source="date" label="Review Date" required />
        <NumberInput 
          source="rating" 
          label="Rating" 
          min={1} 
          max={5} 
          step={0.5}
          required 
        />
        <TextInput 
          source="text" 
          label="Review Text" 
          multiline 
          rows={4} 
          fullWidth 
          required 
        />
        <BooleanInput source="is_published" label="Published" />
      </FormTab>
    </TabbedForm>
  </Edit>
);

// Comment Create Component
export const CommentCreate = props => (
  <Create {...props}>
    <TabbedForm>
      <FormTab label="Details">
        <TextInput source="apartment" label="Apartment ID" required />
        <TextInput source="name" label="Guest Name" required />
        <DateInput source="date" label="Review Date" required />
        <NumberInput 
          source="rating" 
          label="Rating" 
          min={1} 
          max={5} 
          step={0.5}
          required 
        />
        <TextInput 
          source="text" 
          label="Review Text" 
          multiline 
          rows={4} 
          fullWidth 
          required 
        />
        <BooleanInput source="is_published" label="Published" />
      </FormTab>
    </TabbedForm>
  </Create>
);
